// Şifre sıfırlama sayfası
import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { FiLock, FiEye, FiEyeOff, FiCheck } from 'react-icons/fi';

const ResetPassword = () => {
  const { verifyResetToken, resetPassword } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const [token, setToken] = useState('');

  // URL'den token'ı al
  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (tokenParam) {
      setToken(tokenParam);
      verifyToken(tokenParam);
    } else {
      setError('Geçersiz şifre sıfırlama linki');
    }
  }, [searchParams]);

  // Token'ı doğrula
  const verifyToken = async (token) => {
    try {
      setIsLoading(true);
      await verifyResetToken(token);
      setIsVerified(true);
    } catch (error) {
      setError('Token geçersiz veya süresi dolmuş');
    } finally {
      setIsLoading(false);
    }
  };

  // Form değişikliklerini yönet
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  // Form gönderimi
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.password.trim()) {
      setError('Şifre gerekli');
      return;
    }

    if (formData.password.length < 6) {
      setError('Şifre en az 6 karakter olmalı');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Şifreler eşleşmiyor');
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      await resetPassword(token, formData.password);
      setIsSuccess(true);
    } catch (error) {
      setError('Şifre güncellenemedi. Lütfen tekrar deneyin.');
    } finally {
      setIsLoading(false);
    }
  };

  // Başarılı güncelleme sonrası
  if (isSuccess) {
    return (
      <div className="min-h-screen flex">
        {/* Sol taraf - Arka plan görseli */}
        <div 
          className="hidden lg:flex lg:w-3/5 relative overflow-hidden items-center justify-center"
          style={{ backgroundColor: '#dce6ff' }}
        >
          {/* Arka plan görseli - büyütülmüş ve ortalanmış */}
          <div 
            className="w-3/5 h-3/5 bg-contain bg-center bg-no-repeat"
            style={{
              backgroundImage: 'url(/login_background.jpg)',
              backgroundSize: 'contain',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            }}
          ></div>
        </div>

        {/* Sağ taraf - Başarı mesajı */}
        <div className="w-full lg:w-2/5 flex items-center justify-center bg-white p-4 sm:p-8">
          <div className="w-full max-w-md space-y-6 sm:space-y-8">
            {/* Logo */}
            <div className="text-left">
              <div className="mb-4">
                <img 
                  src="/apro_login_logo.png" 
                  alt="APRO Login Logo" 
                  className="w-auto h-16 object-contain"
                />
              </div>
            </div>

            {/* Başarı mesajı */}
            <div className="space-y-6">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                  <FiCheck className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="mt-4 text-lg font-semibold text-gray-900">
                  Şifre Güncellendi
                </h3>
                <p className="mt-2 text-sm text-gray-600">
                  Şifreniz başarıyla güncellendi. Artık yeni şifrenizle giriş yapabilirsiniz.
                </p>
              </div>

              <Link
                to="/login"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg font-semibold transition-all duration-200 shadow-lg flex items-center justify-center"
              >
                Giriş Sayfasına Git
              </Link>
            </div>

            {/* Footer */}
            <div className="text-center pt-4 sm:pt-8">
              <p className="text-xs sm:text-sm text-gray-500">
                All rights reserved. © Copyright 2025 | APRO
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Token doğrulama yükleniyor
  if (isLoading && !isVerified) {
    return (
      <div className="min-h-screen flex">
        {/* Sol taraf - Arka plan görseli */}
        <div 
          className="hidden lg:flex lg:w-3/5 relative overflow-hidden items-center justify-center"
          style={{ backgroundColor: '#dce6ff' }}
        >
          {/* Arka plan görseli - büyütülmüş ve ortalanmış */}
          <div 
            className="w-3/5 h-3/5 bg-contain bg-center bg-no-repeat"
            style={{
              backgroundImage: 'url(/login_background.jpg)',
              backgroundSize: 'contain',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            }}
          ></div>
        </div>

        {/* Sağ taraf - Loading */}
        <div className="w-full lg:w-2/5 flex items-center justify-center bg-white p-4 sm:p-8">
          <div className="w-full max-w-md space-y-6 sm:space-y-8">
            {/* Logo */}
            <div className="text-left">
              <div className="mb-4">
                <img 
                  src="/apro_login_logo.png" 
                  alt="APRO Login Logo" 
                  className="w-auto h-16 object-contain"
                />
              </div>
            </div>

            {/* Loading mesajı */}
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900">
                Token doğrulanıyor...
              </h3>
              <p className="mt-2 text-sm text-gray-600">
                Lütfen bekleyin
              </p>
            </div>

            {/* Footer */}
            <div className="text-center pt-4 sm:pt-8">
              <p className="text-xs sm:text-sm text-gray-500">
                All rights reserved. © Copyright 2025 | APRO
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Hata durumu
  if (error && !isVerified) {
    return (
      <div className="min-h-screen flex">
        {/* Sol taraf - Arka plan görseli */}
        <div 
          className="hidden lg:flex lg:w-3/5 relative overflow-hidden items-center justify-center"
          style={{ backgroundColor: '#dce6ff' }}
        >
          {/* Arka plan görseli - büyütülmüş ve ortalanmış */}
          <div 
            className="w-3/5 h-3/5 bg-contain bg-center bg-no-repeat"
            style={{
              backgroundImage: 'url(/login_background.jpg)',
              backgroundSize: 'contain',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            }}
          ></div>
        </div>

        {/* Sağ taraf - Hata mesajı */}
        <div className="w-full lg:w-2/5 flex items-center justify-center bg-white p-4 sm:p-8">
          <div className="w-full max-w-md space-y-6 sm:space-y-8">
            {/* Logo */}
            <div className="text-left">
              <div className="mb-4">
                <img 
                  src="/apro_login_logo.png" 
                  alt="APRO Login Logo" 
                  className="w-auto h-16 object-contain"
                />
              </div>
            </div>

            {/* Hata mesajı */}
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-red-600">
                  Hata
                </h3>
                <p className="mt-2 text-sm text-red-600">
                  {error}
                </p>
                <p className="mt-4 text-sm text-gray-600">
                  Şifre sıfırlama linki geçersiz veya süresi dolmuş. 
                  Lütfen yeni bir şifre sıfırlama talebi oluşturun.
                </p>
              </div>

              <Link
                to="/forgot-password"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg font-semibold transition-all duration-200 shadow-lg flex items-center justify-center"
              >
                Yeni Şifre Sıfırlama Talebi
              </Link>
            </div>

            {/* Footer */}
            <div className="text-center pt-4 sm:pt-8">
              <p className="text-xs sm:text-sm text-gray-500">
                All rights reserved. © Copyright 2025 | APRO
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Sol taraf - Arka plan görseli */}
      <div 
        className="hidden lg:flex lg:w-3/5 relative overflow-hidden items-center justify-center"
        style={{ backgroundColor: '#dce6ff' }}
      >
        {/* Arka plan görseli - büyütülmüş ve ortalanmış */}
        <div 
          className="w-3/5 h-3/5 bg-contain bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(/login_background.jpg)',
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        ></div>
      </div>

      {/* Sağ taraf - Reset password formu */}
      <div className="w-full lg:w-2/5 flex items-center justify-center bg-white p-4 sm:p-8">
        <div className="w-full max-w-md space-y-6 sm:space-y-8">
          {/* Logo */}
          <div className="text-left">
            <div className="mb-4">
              <img 
                src="/apro_login_logo.png" 
                alt="APRO Login Logo" 
                className="w-auto h-16 object-contain"
              />
            </div>
          </div>

          {/* Başlık */}
          <div className="space-y-2">
            <h2 className="text-xl sm:text-2xl font-bold text-blue-600">Yeni Şifre Belirle</h2>
            <p className="text-sm sm:text-base text-gray-600">Yeni şifrenizi girin</p>
          </div>

          {/* Form */}
          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* Yeni şifre */}
            <div className="space-y-2">
              <label htmlFor="password" className="block text-sm font-semibold text-gray-700">
                Yeni Şifre
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <FiLock className="h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  className={`w-full pl-12 pr-12 py-3 border-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 ${
                    error 
                      ? 'border-red-500 focus:ring-red-500 focus:border-red-500' 
                      : 'border-gray-300'
                  }`}
                  placeholder="Yeni şifrenizi girin"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <FiEyeOff className="h-5 w-5" />
                  ) : (
                    <FiEye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Şifre tekrar */}
            <div className="space-y-2">
              <label htmlFor="confirmPassword" className="block text-sm font-semibold text-gray-700">
                Şifre Tekrar
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <FiLock className="h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                </div>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={`w-full pl-12 pr-12 py-3 border-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 ${
                    error 
                      ? 'border-red-500 focus:ring-red-500 focus:border-red-500' 
                      : 'border-gray-300'
                  }`}
                  placeholder="Şifrenizi tekrar girin"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <FiEyeOff className="h-5 w-5" />
                  ) : (
                    <FiEye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            {error && (
              <p className="text-sm text-red-600 flex items-center">
                <span className="w-1 h-1 bg-red-600 rounded-full mr-2"></span>
                {error}
              </p>
            )}

            {/* Güncelle butonu */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg h-12 flex items-center justify-center"
            >
              {isLoading ? (
                <span>Güncelleniyor...</span>
              ) : (
                'Şifreyi Güncelle'
              )}
            </button>

            {/* Geri dön linki */}
            <div className="text-center pt-2">
              <Link
                to="/login"
                className="text-sm text-gray-600 hover:text-blue-500 font-medium transition-colors duration-200"
              >
                Giriş Sayfasına Dön
              </Link>
            </div>
          </form>

          {/* Footer */}
          <div className="text-center pt-4 sm:pt-8">
            <p className="text-xs sm:text-sm text-gray-500">
              All rights reserved. © Copyright 2025 | APRO
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
